import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTable, useSortBy, useFilters } from 'react-table';
import { toast } from '../utils/toast';
import { searchGrants, defaultGrantCategories } from '../services/grantService';
import { writeGrant } from '../services/grantWritingService';
import { BetaBadge } from '../components/BetaDisclaimer';
import { useNavigate } from 'react-router-dom';

export function GrantFinder({ searchParams }) {
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [savedGrants, setSavedGrants] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [requirementsModal, setRequirementsModal] = useState({ isOpen: false, content: '', title: '', description: '' });
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    
    const navigate = useNavigate();

    // Initial system message to guide the conversation
    useEffect(() => {
        setMessages([
            {
                role: 'assistant',
                content: "Hi! I'm your grant search assistant. I can help you find grants that match your needs. Tell me about your organization and what kind of funding you're looking for. For example:\n\n• What type of organization are you?\n• What's your focus area or project?\n• Do you have a specific funding amount in mind?\n• Any location or deadline requirements?"
            }
        ]);
    }, []);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!currentMessage.trim() || isThinking) return;

        const userMessage = currentMessage.trim();
        setCurrentMessage('');
        setIsThinking(true);

        // Add user message to chat
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

        try {
            // Extract search parameters from the conversation
            const searchQuery = `${userMessage} grants funding opportunities`;
            
            // Search for grants
            const results = await searchGrants(searchQuery);
            setSearchResults(results);

            // Generate a response based on the results
            let response = '';
            if (results.length === 0) {
                response = "I couldn't find any grants matching your criteria. Could you provide more details about what you're looking for? For example:\n• The specific field or sector\n• Your organization type\n• The approximate funding amount needed";
            } else {
                response = `I found ${results.length} grants that might interest you. They're displayed below. Would you like me to:\n• Filter these results further?\n• Provide more details about any specific grant?\n• Search for different types of grants?`;
            }

            // Add assistant response to chat
            setMessages(prev => [...prev, { role: 'assistant', content: response }]);
        } catch (error) {
            console.error('Error searching grants:', error);
            setError(error.message || 'Failed to search grants');
            toast.error(error.message || 'Failed to search grants');
            
            // Add error message to chat
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "I encountered an error while searching for grants. Could you try rephrasing your request?"
            }]);
        } finally {
            setIsThinking(false);
        }
    };

    const handleSearch = useCallback(async (e) => {
        if (e) {
            e.preventDefault();
        }
        setIsLoading(true);
        setError(null);

        try {
            // Construct search query based on filters
            const queryParts = [];
            
            if (searchParams.query.focusAreas.length > 0) {
                queryParts.push(`grant ${searchParams.query.focusAreas.join(' ')} funding`);
            }
            
            if (searchParams.query.organizationType.length > 0) {
                queryParts.push(`for ${searchParams.query.organizationType.join(' or ')}`);
            }

            if (searchParams.query.location) {
                queryParts.push(`in ${searchParams.query.location}`);
            }

            if (searchParams.query.grantType.length > 0) {
                queryParts.push(`${searchParams.query.grantType.join(' or ')} grants`);
            }

            const fundingRange = searchParams.query.fundingRange;
            if (fundingRange.min || fundingRange.max) {
                const range = [];
                if (fundingRange.min) range.push(`minimum $${fundingRange.min}`);
                if (fundingRange.max) range.push(`maximum $${fundingRange.max}`);
                queryParts.push(`funding ${range.join(' to ')}`);
            }

            if (searchParams.query.deadline) {
                queryParts.push(`deadline after ${searchParams.query.deadline}`);
            }

            // Combine all parts into a single search query
            const searchQuery = queryParts.join(' ');

            // Simulate API call with enhanced parameters
            const response = await simulateGrantSearch(searchQuery, searchParams);
            setSearchResults(response);
            
            if (response.length === 0) {
                toast.info('No grants found matching your criteria. Try adjusting your filters.');
            } else {
                toast.success(`Found ${response.length} matching grants!`);
            }
        } catch (error) {
            console.error('Error searching grants:', error);
            toast.error('Failed to search grants. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [searchParams]);

    const simulateGrantSearch = async (query, params) => {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        // This would be replaced with actual API calls in production
        // Mock grant data for demonstration
        return [
            {
                id: 1,
                title: "Healthcare Research Grant",
                organization: "National Health Foundation",
                amount: "$250,000",
                deadline: "2024-06-30",
                description: "Funding for innovative healthcare research projects.",
                eligibility: "Research institutions and healthcare organizations",
                url: "https://example.com/grants/healthcare",
                status: "open"
            },
            {
                id: 2,
                title: "Community Development Initiative",
                organization: "Urban Progress Foundation",
                amount: "$100,000",
                deadline: "2024-07-15",
                description: "Supporting community revitalization projects.",
                eligibility: "Nonprofit organizations and community groups",
                url: "https://example.com/grants/community",
                status: "open"
            },
            {
                id: 3,
                title: "Innovation Technology Grant",
                organization: "Tech Forward Alliance",
                amount: "$500,000",
                deadline: "2024-08-01",
                description: "Funding for technological innovations with social impact.",
                eligibility: "Technology startups and research institutions",
                url: "https://example.com/grants/technology",
                status: "open"
            }
        ];
    };

    const saveGrant = (grant) => {
        setSavedGrants([...savedGrants, grant]);
        toast.success('Grant saved for later reference!');
    };

    const handleWriteGrant = async (grant) => {
        navigate('/grant-writer', { 
            state: { 
                grant: grant
            }
        });
    };

    // For simplicity of this demonstration, rendering a basic UI
    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <div className="flex items-center gap-2 mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Grant Finder</h2>
                <BetaBadge />
            </div>

            {/* Chat interface */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="mb-4 h-64 overflow-y-auto p-3 bg-white rounded border">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`mb-3 ${msg.role === 'user' ? 'text-right' : ''}`}>
                            <div className={`inline-block p-3 rounded-lg ${
                                msg.role === 'user' 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : 'bg-gray-100 text-gray-800'
                            }`}>
                                {msg.content}
                            </div>
                        </div>
                    ))}
                    {isThinking && (
                        <div className="text-gray-500 italic">Thinking...</div>
                    )}
                </div>
                
                <form onSubmit={handleSendMessage} className="flex gap-2">
                    <input
                        type="text"
                        value={currentMessage}
                        onChange={e => setCurrentMessage(e.target.value)}
                        placeholder="Describe the grant you're looking for..."
                        className="flex-1 p-2 border rounded"
                        disabled={isThinking}
                    />
                    <button
                        type="submit"
                        disabled={isThinking || !currentMessage.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                    >
                        Send
                    </button>
                </form>
            </div>

            {/* Results section */}
            {searchResults.length > 0 && (
                <div>
                    <h3 className="text-xl font-semibold mb-4">Found Grants ({searchResults.length})</h3>
                    <div className="space-y-4">
                        {searchResults.map(grant => (
                            <div key={grant.id} className="p-4 border rounded-lg hover:bg-gray-50">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="text-lg font-medium text-blue-700">{grant.title}</h4>
                                    <span className="text-green-600 font-semibold">{grant.amount}</span>
                                </div>
                                <p className="text-gray-600 mb-2">{grant.description}</p>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-500">Deadline: {grant.deadline}</span>
                                    <div className="space-x-2">
                                        <button 
                                            onClick={() => saveGrant(grant)}
                                            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full"
                                        >
                                            Save
                                        </button>
                                        <button 
                                            onClick={() => handleWriteGrant(grant)}
                                            className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full"
                                        >
                                            Write Proposal
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* No results message */}
            {searchTerm && !isLoading && searchResults.length === 0 && (
                <div className="text-center p-4 bg-yellow-50 text-yellow-700 rounded-lg">
                    No grants found matching your criteria. Try adjusting your search or filters.
                </div>
            )}
        </div>
    );
} 